import { useState } from "react";

import { Button, Typography } from "@mui/material";
import moment from "moment";
import { useForm } from "react-hook-form";

import { TPaymentRequest } from "../../../../../@types";
import { apis, callApi } from "../../../../../api";
import { useAccount } from "../../../../../hooks/useAccount";
import { usePaymentRequest } from "../../../../../hooks/usePaymentRequest";
import { USDFormatter } from "../../../../../utils";
import { CommonInput } from "../../../../common";

export type TPaymentRequestItemProps = {
	paymentRequest: TPaymentRequest;
};

export type TCancelPaymentRequestFieldValues = {
	reason: string;
};

export function PaymentRequestItem(props: TPaymentRequestItemProps) {
	const { account } = useAccount();
	const [isCancelling, setIsCancelling] = useState(false);
	const { control, reset, handleSubmit } =
		useForm<TCancelPaymentRequestFieldValues>({
			defaultValues: {
				reason: "",
			},
			mode: "onChange",
		});
	const { cancelPaymentRequest } = usePaymentRequest();
	const isMyPaymentRequest = props.paymentRequest.userId === account?.id;

	const onClickPayNow = async () => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: `${apis.BILL}/${props.paymentRequest.id}`,
			});

			if (response?.paymentResult?.url) {
				window.location.href = response.paymentResult.url;
			}
		} catch (error) {}
	};

	const onClickCancelPaymentRequest = () => {
		setIsCancelling(true);
	};

	const onClickCancelCancelPaymentRequest = () => {
		reset();
		setIsCancelling(false);
	};

	const onClickSubmitCancelRequest = async (
		data: TCancelPaymentRequestFieldValues,
	) => {
		try {
			await cancelPaymentRequest(props.paymentRequest.id, {
				cancelReason: data.reason.trim(),
			});
			onClickCancelCancelPaymentRequest();
		} catch (error) {}
	};

	return (
		<div
			className="payment-request-item-container"
			key={props.paymentRequest.id}>
			<Typography className="normal-text">
				{"Course: "}
				<Typography component="span" className="normal-text bold">
					{props.paymentRequest.summary.course.name}
				</Typography>
			</Typography>
			{isMyPaymentRequest && (
				<Typography className="normal-text">
					{"Instructor: "}
					<Typography component="span" className="normal-text bold">
						{`${props.paymentRequest.teacher.firstName} ${props.paymentRequest.teacher.lastName}`}
					</Typography>
				</Typography>
			)}
			{!isMyPaymentRequest && (
				<Typography className="normal-text">
					{"Student: "}
					<Typography component="span" className="normal-text bold">
						{`${props.paymentRequest.user.firstName} ${props.paymentRequest.user.lastName}`}
					</Typography>
				</Typography>
			)}
			<Typography className="normal-text">
				{"Total cost: "}
				<Typography component="span" className="normal-text bold">
					{USDFormatter.format(props.paymentRequest.totalCost / 100)}
				</Typography>
			</Typography>
			<Typography className="normal-text">
				{"Message: "}
				<Typography component="span" className="normal-text bold">
					{props.paymentRequest.message}
				</Typography>
			</Typography>
			<Typography className="normal-text">
				{"Requested at: "}
				<Typography component="span" className="normal-text bold">
					{moment(props.paymentRequest.createdAt).format("MMM DD YYYY, HH:mm")}
				</Typography>
			</Typography>
			{props.paymentRequest.status === "Cancelled" &&
				props.paymentRequest.cancelReason && (
					<Typography className="normal-text">
						{"Cancellation reason: "}
						<Typography component="span" className="normal-text bold">
							{props.paymentRequest.cancelReason}
						</Typography>
					</Typography>
				)}
			{isMyPaymentRequest && props.paymentRequest.status === "Issued" && (
				<Button
					className="normal-button primary-button pay-button no-transform item-separator-tiny"
					onClick={onClickPayNow}>
					Send payment
				</Button>
			)}
			{!isMyPaymentRequest &&
				props.paymentRequest.status === "Issued" &&
				!isCancelling && (
					<Button
						className="normal-button disabled-button pay-button no-transform item-separator-tiny"
						onClick={onClickCancelPaymentRequest}>
						Cancel request
					</Button>
				)}
			{!isMyPaymentRequest &&
				props.paymentRequest.status === "Issued" &&
				isCancelling && (
					<>
						<div className="separator" />
						<Typography className="normal-text bold">
							Cancellation reason
						</Typography>
						<CommonInput
							control={control}
							name="reason"
							placeholder="Cancellation reason"
							rules={{
								required: {
									value: true,
									message: "Cancellation reason is required.",
								},
								validate: (value) => {
									if (value.trim().length === 0) {
										return "Cancellation reason is required.";
									}
								},
							}}
						/>
						<div className="cancel-request-button-group item-separator-tiny">
							<Button
								className="normal-button disabled-button no-transform"
								onClick={onClickCancelCancelPaymentRequest}>
								Cancel
							</Button>
							<Button
								className="normal-button primary-button no-transform"
								onClick={handleSubmit(onClickSubmitCancelRequest)}>
								Cancel request
							</Button>
						</div>
					</>
				)}
		</div>
	);
}
